/**
 * What the simulator was opened with and what it has on screen. A caller hands over a set
 * of cases and which one to open; nothing producer-specific passes this point, so back
 * testing drives the simulator without the simulator importing it. Nothing here is stored:
 * the URL that caused a hand-off arms it again after a reload.
 */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { tabStorage } from '@/lib/tab-storage';
import type { ChatMessage, ScenarioKind } from '@/types/websocket.types';
import type { RunDetails, ThinkingStep } from '@/stores/websocket.store';
import type { DescribedScenarioType } from '@/services/ai-function.service';
import type { ScenarioCase } from './scenario-case';
import type { RunReview } from '../review/review.store';
import type { LoadedThread } from '../conversations/loaded-thread';

interface SimulatorOrigin {
  /** The SOP these cases belong to, so clearing it can end the session. */
  sopId?: string;
}

export interface SimulatorSession {
  /** Every case the loader offers, in the order they were handed over. */
  cases: ScenarioCase[];
  /** Which case is on screen; null once the simulator has been reset. */
  activeIndex: number | null;
  origin: SimulatorOrigin | null;
  /** Identifies this hand-off, so re-opening the same case loads it again. */
  id: string;
}

export const simulatorSessionAtom = atom<SimulatorSession | null>(null);

/**
 * The hand-off the screen last acted on, and the case that put on screen (its
 * `caseRunKey`, null when the screen holds no case). Kept here, not in the workbench,
 * which unmounts whenever someone leaves the page: a case loads when the hand-off
 * changes, never just because the workbench mounted again.
 */
export const actedOnSessionAtom = atom<SimulatorSession | null>(null);
export const caseOnScreenAtom = atom<string | null>(null);

/**
 * The conversation on screen: its id, the channel it goes out on and the past
 * conversation it was loaded from. Kept here for the same reason, so coming back
 * continues it instead of starting a new one.
 */
export const conversationIdAtom = atom<string>(crypto.randomUUID());
export const channelIdAtom = atom<string>('');
export const loadedThreadAtom = atom<LoadedThread | null>(null);

/** The kept run on screen, reopened or last asked here. The address names it. */
export const runOnScreenAtom = atom<string | null>(null);

/** A scenario described in words for a thread with nothing behind it: the situation and the type written from it. */
interface DescribedScenario {
  situation: string;
  scenarioType: DescribedScenarioType | null;
}

export const EMPTY_DESCRIBED_SCENARIO: DescribedScenario = { situation: '', scenarioType: null };

/** Kept per tab like the thread and the world it goes with, so a reload does not run the thread unbent. Reset drops it with them. */
export const describedScenarioAtom = atomWithStorage<DescribedScenario>(
  'sim-described-kim-ai',
  EMPTY_DESCRIBED_SCENARIO,
  tabStorage<DescribedScenario>(),
  { getOnInit: true }
);

/** What a reopened run was a run of. Runs asked after it carry the same links. */
interface RunLinks {
  sopId: string | null;
  scenarioId: string | null;
  ticketId: string | null;
  kind: ScenarioKind | null;
}

export const reopenedLinksAtom = atom<RunLinks | null>(null);

/**
 * What a case looked like when you last left it, so switching away and back
 * does not discard the answer that was the point of running it.
 */
export interface CaseRun {
  messages: ChatMessage[];
  conversationId: string;
  thinkingSteps: ThinkingStep[];
  runDetails: RunDetails | null;
  /** The reviews of this case's replies, keyed by requestId; a verdict is part of the answer the run was for. */
  reviews: Record<string, RunReview>;
  /** When this run was last put away, for evicting the coldest first. */
  savedAt?: number;
}

/** Runs remembered in memory before the coldest is dropped. Not persisted: a reload starts fresh. */
const MAX_REMEMBERED_RUNS = 40;

export const caseRunKey = (sessionId: string, index: number): string => `${sessionId}:${index}`;

export const caseRunsAtom = atom<Record<string, CaseRun>>({});

/**
 * The conversation id a fresh run of this scenario gets: `<scenarioId>:<nonce>`, so the
 * scenario stays legible in a trace while every run is its own conversation and Brahma
 * carries no state between them. A random nonce, since a count would have to be shared
 * across tabs and reloads to stay unique.
 */
export const nextConversationId = (scenarioId: string): string =>
  `${scenarioId}:${crypto.randomUUID().slice(0, 8)}`;

/** Remembers where a case was left, dropping the coldest runs past the cap. */
export const rememberCaseRunAtom = atom(null, (get, set, key: string, run: CaseRun) => {
  const next: Record<string, CaseRun> = { ...get(caseRunsAtom), [key]: { ...run, savedAt: Date.now() } };
  const keys = Object.keys(next);
  if (keys.length > MAX_REMEMBERED_RUNS) {
    const coldestFirst = keys.sort((a, b) => (next[a].savedAt ?? 0) - (next[b].savedAt ?? 0));
    for (const stale of coldestFirst.slice(0, keys.length - MAX_REMEMBERED_RUNS)) delete next[stale];
  }
  set(caseRunsAtom, next);
});

/** Hands a set of cases to the simulator. The caller navigates; this only arms it. */
export const openSimulatorSessionAtom = atom(
  null,
  (_get, set, session: Omit<SimulatorSession, 'id'>) => {
    // Remembered runs belong to the previous hand-off.
    set(caseRunsAtom, {});
    set(simulatorSessionAtom, { ...session, id: crypto.randomUUID() });
  }
);

/**
 * Drops the hand-off entirely: no set, no remembered runs. For when the cases
 * themselves stop existing (their scenarios were cleared), as opposed to
 * `clearActiveCaseAtom`, which only unloads the one on screen.
 */
export const endSimulatorSessionAtom = atom(null, (_get, set) => {
  set(caseRunsAtom, {});
  set(simulatorSessionAtom, null);
  set(actedOnSessionAtom, null);
  set(caseOnScreenAtom, null);
});

/**
 * Reset empties the simulator, not the session: the set stays available and
 * navigable, only nothing is loaded from it.
 */
export const clearActiveCaseAtom = atom(null, (get, set) => {
  const session = get(simulatorSessionAtom);
  if (!session) return;
  // Its remembered run goes too, or re-picking the case would restore the very
  // thread the reset just cleared.
  if (session.activeIndex !== null) {
    const runs = { ...get(caseRunsAtom) };
    delete runs[caseRunKey(session.id, session.activeIndex)];
    set(caseRunsAtom, runs);
  }
  set(simulatorSessionAtom, { ...session, activeIndex: null });
});
